import { Injectable, OnDestroy } from "@angular/core";
import { BehaviorSubject, Observable, Subscription, forkJoin, from, map, of, take, tap } from "rxjs";
import { NumberingTypes, Utils, DocumentHelper } from 'fakturnia-shared';
import { DbService } from "./db.service";
import { DataSynchronizerService } from "./data-synchronization.service";
import { AppDb } from "fakturnia-shared";
import { DbDocument } from "fakturnia-shared";
import { Invoice } from "fakturnia-shared";
import { DateTime } from "luxon";

@Injectable({
    providedIn: 'root'
})
export class DocumentsService implements OnDestroy {

    private _subscriptions: Subscription[] = [];

    private _documents = new BehaviorSubject<DbDocumentObject>({});
    currentDocuments = this._documents.asObservable();

    private _db: AppDb

    ngOnDestroy(): void {
        this._subscriptions.forEach(sub => sub.unsubscribe())
    }

    constructor(
        private _dbService: DbService,
        private _dataSynchronizerService: DataSynchronizerService
    ) {
        this._subscriptions.push(this._dbService.getDatabase().subscribe({
            next: (data) => {
                // Reset cache za każdym razem gdy baza się zmienia (logout/switch konta).
                this._db = data
                this._documents.next({})
                if (data) {
                    this.getAll().pipe(take(1)).subscribe()
                }
            }
        }))

        this._subscriptions.push(this._dataSynchronizerService.currentData.subscribe({
            next: (data) => {
                if (!data) return
                if (data.table != 'documents') return

                // Update
                data.value.forEach((document: DbDocument) => {
                    this._documents.value[document._id] = document
                });

                // Push changes
                this._documents.next(this._documents.value)

                this._dataSynchronizerService.received()
            }
        }))
    }

    public getOldestInvoice(): DbDocument | null {
        const invoices = Object.entries(this._documents.value).map((x: [any, any]) => x[1]).filter(x => !x.isDeleted && x.isPaid)
        if (invoices.length == 0) return null

        let oldestInvoice = invoices[0]
        invoices.forEach((invoice: any) => {
            if (DateTime.fromISO(invoice.issueDate).toMillis() < DateTime.fromISO(oldestInvoice.issueDate).toMillis()) {
                oldestInvoice = invoice
            }
        })

        return oldestInvoice
    }

    /**
     * Zwraca najstarszy dokument kosztowy (expense)
     */
    public getOldestExpenseDocument(): DbDocument | null {
        const expenses = Object.entries(this._documents.value)
            .map((x: [any, DbDocument]) => x[1])
            .filter(x => !x.isDeleted && DocumentHelper.getDirection(x) === 'expense');
        
        if (expenses.length === 0) return null;

        let oldest = expenses[0];
        expenses.forEach((doc: DbDocument) => {
            if (DateTime.fromISO(doc.issueDate).toMillis() < DateTime.fromISO(oldest.issueDate).toMillis()) {
                oldest = doc;
            }
        });

        return oldest;
    }

    /**
     * Zwraca wszystkie dokumenty z danego miesiąca/roku (bez filtrowania po typie)
     */
    getAllDocumentsByMonthAndYear(month: number, year: number): DbDocument[] {
        return Object.entries(this._documents.value)
            .map((x: [any, DbDocument]) => x[1])
            .filter(x => {
                const issueDate = DateTime.fromISO(x.issueDate);
                return !x.isDeleted &&
                    issueDate.month === month &&
                    issueDate.year === year;
            });
    }

    /**
     * Zwraca wszystkie dokumenty z danego dnia/miesiąca/roku (bez filtrowania po typie)
     */
    getAllDocumentsByDayMonthAndYear(day: number, month: number, year: number): DbDocument[] {
        return Object.entries(this._documents.value)
            .map((x: [any, DbDocument]) => x[1])
            .filter(x => {
                const issueDate = DateTime.fromISO(x.issueDate);
                return !x.isDeleted &&
                    issueDate.day === day &&
                    issueDate.month === month &&
                    issueDate.year === year;
            });
    }
    
    /**
     * Zwraca wszystkie dokumenty synchronicznie (bez Observable)
     */
    public getAllDocumentsSync(): DbDocument[] {
        return Object.values(this._documents.value).filter(x => !x.isDeleted);
    }
    
    countIssuedDocuments(): number {
        // Liczy wszystkie dokumenty (przychody i koszty) które nie są usunięte
        return Object.entries(this._documents.value).map((x: [any, any]) => x[1]).filter(x => !x.isDeleted).length
    }

    getDocumentsByMonthAndYear(docTypes: string[], month: number, year: number): DbDocument[] {
        return Object.entries(this._documents.value)
            .map((x: [any, DbDocument]) => x[1])
            .filter(x => {
                const issueDate = DateTime.fromISO(x.issueDate); // Parse issueDate using Luxon
                return docTypes.includes(x.documentType) &&
                    !x.isDeleted &&
                    x.isPaid &&
                    issueDate.month === month&& // Luxon's month is 1-based, so adjust for 0-based input
                    issueDate.year === year;
            });
    }

    getDocumentsByDayMonthAndYear(docTypes: string[], day: number, month: number, year: number): DbDocument[] {
        return Object.entries(this._documents.value)
            .map((x: [any, DbDocument]) => x[1])
            .filter(x => {
                const issueDate = DateTime.fromISO(x.issueDate); // Parse issueDate using Luxon
                return docTypes.includes(x.documentType) &&
                    !x.isDeleted &&
                    x.isPaid &&
                    issueDate.day === day && // Match day
                    issueDate.month === month&& // Luxon's month is 1-based, adjust for 0-based input
                    issueDate.year === year; // Match year
            });
    }

    public getNextDocumentNo(invoice: Invoice, numberingType: NumberingTypes): number {
        const issueDate = !Utils.isNullOrEmpty(invoice.issueDateHeader.value)
            ? DateTime.fromISO(invoice.issueDateHeader.value)
            : DateTime.now();

        const currentMonth = issueDate.month;
        const currentYear = issueDate.year;

        // Wspólna numeracja: FakturaKorygujaca, FakturaKorygujacaZaliczkowa i FakturaKorygujacaRozliczeniowa
        const sharedNumberingTypes = ['FakturaKorygujaca', 'FakturaKorygujacaZaliczkowa', 'FakturaKorygujacaRozliczeniowa'];
        const matchesDocType = (docType: string) => {
            if (sharedNumberingTypes.includes(invoice.documentType)) {
                return sharedNumberingTypes.includes(docType);
            }
            return docType === invoice.documentType;
        };

        // Filter documents based on numbering type
        // Pomijamy dokumenty kosztowe z KSeF (source='ksef' + direction='expense') - nie wpływają na numerację
        const documents = Object.entries(this._documents.value)
            .map((x: [any, DbDocument]) => x[1])
            .filter(doc => {
                const docDate = DateTime.fromISO(doc.issueDate);
                const isSameYear = docDate.year === currentYear;
                const isSameMonth = docDate.month === currentMonth;

                return (
                    matchesDocType(doc.documentType) &&
                    !doc.isDeleted &&
                    !(doc.source === 'ksef' && doc.direction === 'expense') &&
                    isSameYear &&
                    (numberingType === NumberingTypes.YEAR || isSameMonth)
                );
            });

        // Get the highest document number in the filtered documents
        const maxDocumentNo = documents.reduce((max, doc) => {
            return Math.max(max, doc.documentNo || 0);
        }, 0);

        // If no documents exist for the year/month, use getNextDocumentNumber
        if (maxDocumentNo === 0) {
            const { nextNumber } = this.getNextDocumentNumber(invoice);
            return nextNumber;
        }

        // Return the next document number
        return maxDocumentNo + 1;
    }

    getNextDocumentNumber(invoice: Invoice): { nextNumber: number, currentMonth: number, currentYear: number } {
        let nextNumber = 1;
        const currentMonth = !Utils.isNullOrEmpty(invoice.issueDateHeader.value) ? new Date(invoice.issueDateHeader.value).getMonth() : new Date().getMonth();
        const currentYear = !Utils.isNullOrEmpty(invoice.issueDateHeader.value) ? new Date(invoice.issueDateHeader.value).getFullYear() : new Date().getFullYear();

        // Wspólna numeracja: FakturaKorygujaca, FakturaKorygujacaZaliczkowa i FakturaKorygujacaRozliczeniowa
        const sharedNumberingTypes = ['FakturaKorygujaca', 'FakturaKorygujacaZaliczkowa', 'FakturaKorygujacaRozliczeniowa'];
        const matchesDocType = (docType: string) => {
            if (sharedNumberingTypes.includes(invoice.documentType)) {
                return sharedNumberingTypes.includes(docType);
            }
            return docType === invoice.documentType;
        };

        // Pomijamy dokumenty kosztowe z KSeF (source='ksef' + direction='expense') - nie wpływają na numerację
        const documents = Object.entries(this._documents.value).map((x: [any, DbDocument]) => x[1]).filter(x => matchesDocType(x.documentType) && !x.isDeleted && !(x.source === 'ksef' && x.direction === 'expense') && new Date(x.issueDate).getMonth() == currentMonth && new Date(x.issueDate).getFullYear() == currentYear).length

        if (documents > 0) {
            nextNumber = documents + 1;
        }

        return {
            nextNumber: nextNumber,
            currentMonth: currentMonth,
            currentYear: currentYear
        }
    }

    getPrepayments() {
        return Object.values(this._documents.value)
            .filter((invoice: DbDocument) => invoice.documentType == 'FakturaZaliczka')
    }

    getById(invoiceId): DbDocument | null {
        const invoice = this._documents.value[invoiceId]

        if (!invoice) {
            console.warn(`Invoice with id: ${invoiceId} not found.`)
            return null
        }
        else return invoice
    }

    /**
     * Aktualizuje dokument w pamięci I w Dexie (lokalnej bazie IndexedDB)
     * Używane np. po pobraniu XML z KSeF, żeby przy następnym otwarciu podglądu był dostępny z cache
     */
    async updateDocumentInMemory(documentId: string, updates: Partial<DbDocument>): Promise<void> {
        const document = this._documents.value[documentId];
        if (!document) {
            console.warn(`[DocumentsService] Cannot update document ${documentId} - not found in memory`);
            return;
        }
        
        // Scal aktualizacje z istniejącym dokumentem
        const updatedDocument = {
            ...document,
            ...updates
        };
        
        this._documents.value[documentId] = updatedDocument;
        
        // Powiadom subskrybentów o zmianie
        this._documents.next(this._documents.value);
        
        // Zapisz również do Dexie (lokalnej bazy IndexedDB)
        if (this._db) {
            try {
                await this._db.documents.put(updatedDocument);
                console.log(`[DocumentsService] Document ${documentId} updated in memory and Dexie`);
            } catch (error) {
                console.error(`[DocumentsService] Failed to save document ${documentId} to Dexie:`, error);
            }
        } else {
            console.log(`[DocumentsService] Document ${documentId} updated in memory (Dexie not available)`);
        }
    }

    browseDocuments({ currentPage, defaultPageSize, activeFilters, filters }): PaginatedDbDocuments {

        let t1 = new Date().getTime();

        let fromIndex = (currentPage * defaultPageSize) - defaultPageSize;
        let toIndex = fromIndex + (defaultPageSize - 1);

        let results: any = Object.entries(this._documents.value)
            .map(x => x[1])

        if (filters.length > 0) {
            filters.forEach(filter => {
                results = results.filter(filter)
            })
        }

        activeFilters.forEach((element: any) => {

            let key = element[0];
            let value = element[1];

            let filterValue = value;
            if (typeof filterValue == 'string') { filterValue = filterValue.toLowerCase() }

            if (key == 'searchText') {
                results = results.filter(
                    (document: DbDocument) => {
                        let found = false;
                        let name = '';
                        let documentNumber = document.documentNumber.toLowerCase();
                        let firstName = '';
                        let lastName = '';
                        let nazwiskoImie = '';
                        let imieNazwisko = '';
                        let nip = '';
                        let grossSum = document.grossSum.toString()
                        let grossSumFixed = document.grossSum.toFixed(2)


                        if (documentNumber.includes(filterValue)) { found = true; return found; }

                        const buyer = document.buyer;
                        if (Utils.isNullOrEmpty(buyer)) return false;
                        if (Utils.isDefined(buyer, "name")) { name = buyer['name'].toLowerCase(); }
                        if (Utils.isDefined(buyer, "firstName")) { firstName = buyer['firstName'].toLowerCase(); }
                        if (Utils.isDefined(buyer, "lastName")) { lastName = buyer['lastName'].toLowerCase(); }
                        if (Utils.isDefined(buyer, "nip")) { nip = buyer['nip'].toLowerCase(); }

                        if (
                            firstName.length > 0 && lastName.length > 0
                        ) {
                            imieNazwisko = `${firstName} ${lastName}`
                            nazwiskoImie = `${lastName} ${firstName}`
                        }

                        if (name.includes(filterValue)) { found = true }
                        else if (firstName.includes(filterValue)) { found = true }
                        else if (imieNazwisko.includes(filterValue)) { found = true }
                        else if (nazwiskoImie.includes(filterValue)) { found = true }
                        else if (nip.includes(filterValue)) { found = true }
                        else if (grossSum.includes(filterValue)) { found = true }
                        else if (grossSumFixed.includes(filterValue)) { found = true }

                        return found;
                    })
            }
        })

        //Sorting
        results.sort((a: any, b: any) => {
            const dateA = new Date(a.issueDate);
            const dateB = new Date(b.issueDate);

            // Compare by year
            if (dateA.getFullYear() !== dateB.getFullYear()) {
                return dateB.getFullYear() - dateA.getFullYear();
            }

            // Compare by month
            if (dateA.getMonth() !== dateB.getMonth()) {
                return dateB.getMonth() - dateA.getMonth();
            }

            // Compare by documentNo
            return parseInt(b.documentNo?.toString() || '0') - parseInt(a.documentNo?.toString() || '0');
        });
        
        if (toIndex > results.length - 1) {
            toIndex = results.length - 1;
        }

        const paginationResult: PaginatedDbDocuments = {
            time: new Date().getTime() - t1,
            allPages: Math.ceil(results.length / defaultPageSize),
            resultsCount: results.length,
            results: Utils.getObjectsInRange(results, fromIndex, toIndex),
        }

        return paginationResult


    }

    /**
     * Czyści cache w pamięci (np. przy wylogowaniu / przełączeniu konta).
     */
    public clear(): void {
        this._documents.next({})
    }

    getAll(): Observable<any> {
        if (!this._db) return of(null)
        const s1 = new Date().getTime();
        const documentLoader = this._db.transaction("r", this._db.documents, () => {
            return this._db.documents.orderBy('createdAt').reverse().toArray();
        }).then((result: DbDocument[]) => {
            const object: DbDocumentObject = {};
            result.forEach(doc => {
                object[doc._id] = doc;
            });
            return object;
        });

        return forkJoin([from(documentLoader)]).pipe(
            map(([documents]) => {
                // Update your subjects
                this._documents.next(documents);
                return { documents };
            }),
            tap(() => console.log(`[Documents Service]: Built in ${new Date().getTime() - s1}ms.`))
        );
    }

}

export interface DbDocumentObject {
    [key: number]: DbDocument;
}

export interface PaginatedDbDocuments {
    results: DbDocument[]
    resultsCount: number
    time: number
    allPages: number
}